"use client"

import { useState } from "react"
import { Search, DollarSign, TrendingUp, BarChart3, Users, MapPin, Award, Building, Info } from "lucide-react"
import { cn } from "@/utils/common"
import { Button } from "@/components/ui/common/Button"
import { Badge } from "@/components/ui/common/Badge"
import { Input } from "@/components/forms/inputs/text/Input"

interface SalaryData {
  id: number
  position: string
  category: string
  experience: "Junior" | "Mid" | "Senior" | "Expert"
  minSalary: number
  maxSalary: number
  averageSalary: number
  currency: string
  region: string
  company: string
  companySize: "Startup" | "Średnia" | "Duża" | "Korporacja"
  benefits: string[]
  growth: number
  demandLevel: "Wysoki" | "Średni" | "Niski"
  remote: boolean
}

interface CategoryStats {
  category: string
  averageSalary: number
  growth: number
  positions: number
  icon: any
  color: string
}

export default function Earnings() {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("Wszystkie")
  const [selectedExperience, setSelectedExperience] = useState("Wszystkie")
  const [selectedRegion, setSelectedRegion] = useState("Wszystkie")
  const [sortBy, setSortBy] = useState("salary")

  const salaryData: SalaryData[] = [
    {
      id: 1,
      position: "Senior Frontend Developer",
      category: "IT",
      experience: "Senior",
      minSalary: 12000,
      maxSalary: 18000,
      averageSalary: 15000,
      currency: "PLN",
      region: "Warszawa",
      company: "TechCorp",
      companySize: "Duża",
      benefits: ["Prywatna opieka medyczna", "Elastyczne godziny", "Praca zdalna"],
      growth: 8.5,
      demandLevel: "Wysoki",
      remote: true,
    },
    {
      id: 2,
      position: "Data Scientist",
      category: "IT",
      experience: "Mid",
      minSalary: 10000,
      maxSalary: 16000,
      averageSalary: 13000,
      currency: "PLN",
      region: "Kraków",
      company: "DataLab",
      companySize: "Średnia",
      benefits: ["Budżet na szkolenia", "Prywatna opieka medyczna", "Karta Multisport"],
      growth: 12.3,
      demandLevel: "Wysoki",
      remote: true,
    },
    {
      id: 3,
      position: "UX Designer",
      category: "Design",
      experience: "Mid",
      minSalary: 8000,
      maxSalary: 12000,
      averageSalary: 10000,
      currency: "PLN",
      region: "Warszawa",
      company: "DesignStudio",
      companySize: "Średnia",
      benefits: ["Elastyczne godziny", "Praca zdalna", "Budżet na konferencje"],
      growth: 6.7,
      demandLevel: "Średni",
      remote: true,
    },
    {
      id: 4,
      position: "Marketing Manager",
      category: "Marketing",
      experience: "Senior",
      minSalary: 9000,
      maxSalary: 14000,
      averageSalary: 11500,
      currency: "PLN",
      region: "Warszawa",
      company: "MarketPro",
      companySize: "Duża",
      benefits: ["Prywatna opieka medyczna", "Samochód służbowy", "Bonus roczny"],
      growth: 5.2,
      demandLevel: "Średni",
      remote: false,
    },
    {
      id: 5,
      position: "DevOps Engineer",
      category: "IT",
      experience: "Senior",
      minSalary: 14000,
      maxSalary: 20000,
      averageSalary: 17000,
      currency: "PLN",
      region: "Gdańsk",
      company: "CloudTech",
      companySize: "Duża",
      benefits: ["Prywatna opieka medyczna", "Elastyczne godziny", "Akcje firmy"],
      growth: 15.8,
      demandLevel: "Wysoki",
      remote: true,
    },
    {
      id: 6,
      position: "HR Business Partner",
      category: "HR",
      experience: "Mid",
      minSalary: 7000,
      maxSalary: 11000,
      averageSalary: 9000,
      currency: "PLN",
      region: "Wrocław",
      company: "HRSolutions",
      companySize: "Średnia",
      benefits: ["Prywatna opieka medyczna", "Elastyczne godziny", "Budżet na rozwój"],
      growth: 4.1,
      demandLevel: "Średni",
      remote: false,
    },
    {
      id: 7,
      position: "Junior Java Developer",
      category: "IT",
      experience: "Junior",
      minSalary: 5000,
      maxSalary: 8000,
      averageSalary: 6500,
      currency: "PLN",
      region: "Poznań",
      company: "JavaSoft",
      companySize: "Średnia",
      benefits: ["Mentoring", "Szkolenia", "Prywatna opieka medyczna"],
      growth: 18.2,
      demandLevel: "Wysoki",
      remote: true,
    },
    {
      id: 8,
      position: "Product Manager",
      category: "Zarządzanie",
      experience: "Senior",
      minSalary: 12000,
      maxSalary: 18000,
      averageSalary: 15000,
      currency: "PLN",
      region: "Warszawa",
      company: "ProductCorp",
      companySize: "Duża",
      benefits: ["Prywatna opieka medyczna", "Elastyczne godziny", "Bonus kwartalne"],
      growth: 7.9,
      demandLevel: "Wysoki",
      remote: true,
    },
  ]

  const categoryStats: CategoryStats[] = [
    {
      category: "IT",
      averageSalary: 12500,
      growth: 13.2,
      positions: 5,
      icon: BarChart3,
      color: "blue",
    },
    {
      category: "Design",
      averageSalary: 10000,
      growth: 6.7,
      positions: 1,
      icon: Award,
      color: "purple",
    },
    {
      category: "Marketing",
      averageSalary: 11500,
      growth: 5.2,
      positions: 1,
      icon: TrendingUp,
      color: "green",
    },
    {
      category: "HR",
      averageSalary: 9000,
      growth: 4.1,
      positions: 1,
      icon: Users,
      color: "orange",
    },
    {
      category: "Zarządzanie",
      averageSalary: 15000,
      growth: 7.9,
      positions: 1,
      icon: Building,
      color: "red",
    },
  ]

  const categories = ["Wszystkie", "IT", "Design", "Marketing", "HR", "Zarządzanie"]
  const experienceLevels = ["Wszystkie", "Junior", "Mid", "Senior", "Expert"]
  const regions = ["Wszystkie", "Warszawa", "Kraków", "Gdańsk", "Wrocław", "Poznań"]

  const filteredData = salaryData
    .filter((item) => {
      const matchesSearch =
        item.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.company.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = selectedCategory === "Wszystkie" || item.category === selectedCategory
      const matchesExperience = selectedExperience === "Wszystkie" || item.experience === selectedExperience
      const matchesRegion = selectedRegion === "Wszystkie" || item.region === selectedRegion
      return matchesSearch && matchesCategory && matchesExperience && matchesRegion
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "salary":
          return b.averageSalary - a.averageSalary
        case "growth":
          return b.growth - a.growth
        case "position":
          return a.position.localeCompare(b.position)
        default:
          return 0
      }
    })

  const getDemandColor = (level: string) => {
    switch (level) {
      case "Wysoki":
        return "text-green-600 bg-green-50 border-green-200"
      case "Średni":
        return "text-yellow-600 bg-yellow-50 border-yellow-200"
      case "Niski":
        return "text-red-600 bg-red-50 border-red-200"
      default:
        return "text-gray-600 bg-gray-50 border-gray-200"
    }
  }

  const getExperienceColor = (level: string) => {
    switch (level) {
      case "Junior":
        return "text-blue-600 bg-blue-50 border-blue-200"
      case "Mid":
        return "text-purple-600 bg-purple-50 border-purple-200"
      case "Senior":
        return "text-orange-600 bg-orange-50 border-orange-200"
      case "Expert":
        return "text-red-600 bg-red-50 border-red-200"
      default:
        return "text-gray-600 bg-gray-50 border-gray-200"
    }
  }

  const getCompanySizeColor = (size: string) => {
    switch (size) {
      case "Startup":
        return "text-green-600 bg-green-50 border-green-200"
      case "Średnia":
        return "text-blue-600 bg-blue-50 border-blue-200"
      case "Duża":
        return "text-purple-600 bg-purple-50 border-purple-200"
      case "Korporacja":
        return "text-red-600 bg-red-50 border-red-200"
      default:
        return "text-gray-600 bg-gray-50 border-gray-200"
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Zarobki w Polsce</h1>
          <p className="text-gray-600">Sprawdź aktualne zarobki na różnych stanowiskach i w różnych regionach</p>
        </div>

        {/* Category Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {categoryStats.map((stat) => (
            <div key={stat.category} className="bg-white p-4 rounded-none border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center mb-2">
                <stat.icon className={cn("h-5 w-5 mr-2", `text-[#2bcaca]`)} />
                <h3 className="font-medium text-gray-900">{stat.category}</h3>
              </div>
              <div className="space-y-1">
                <div className="text-lg font-bold text-gray-900">{stat.averageSalary.toLocaleString()} zł</div>
                <div className="text-sm text-gray-500">{stat.positions} stanowisk</div>
                <div className="flex items-center text-sm">
                  <TrendingUp className="h-3 w-3 text-[#2bcaca] mr-1" />
                  <span className="text-[#2bcaca]">+{stat.growth}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-none border border-gray-200 shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Szukaj stanowiska lub firmy..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-none focus:outline-none focus:ring-2 focus:ring-[#2bcaca]"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Experience Filter */}
            <div>
              <select
                value={selectedExperience}
                onChange={(e) => setSelectedExperience(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-none focus:outline-none focus:ring-2 focus:ring-[#2bcaca]"
              >
                {experienceLevels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>

            {/* Region Filter */}
            <div>
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-none focus:outline-none focus:ring-2 focus:ring-[#2bcaca]"
              >
                {regions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Sort Options */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Sortuj według:</span>
              <div className="flex gap-2">
                <Button
                  variant={sortBy === "salary" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSortBy("salary")}
                  className={cn(
                    sortBy === "salary"
                      ? "bg-[#2bcaca] hover:bg-[#229999] text-white"
                      : "text-gray-600 hover:text-gray-900 border-gray-200",
                  )}
                >
                  Zarobki
                </Button>
                <Button
                  variant={sortBy === "growth" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSortBy("growth")}
                  className={cn(
                    sortBy === "growth"
                      ? "bg-[#2bcaca] hover:bg-[#229999] text-white"
                      : "text-gray-600 hover:text-gray-900 border-gray-200",
                  )}
                >
                  Wzrost
                </Button>
                <Button
                  variant={sortBy === "position" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSortBy("position")}
                  className={cn(
                    sortBy === "position"
                      ? "bg-[#2bcaca] hover:bg-[#229999] text-white"
                      : "text-gray-600 hover:text-gray-900 border-gray-200",
                  )}
                >
                  Nazwa
                </Button>
              </div>
            </div>
            <div className="text-sm text-gray-500">Znaleziono {filteredData.length} stanowisk</div>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {filteredData.map((item) => (
            <div key={item.id} className="bg-white p-6 rounded-none border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                {/* Left Side - Position Info */}
                <div className="flex-1 mb-4 lg:mb-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{item.position}</h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-gray-600">{item.company}</span>
                        <Badge variant="outline" className={getCompanySizeColor(item.companySize)} size="sm">
                          {item.companySize}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Badge variant="outline" className={getExperienceColor(item.experience)}>
                      {item.experience}
                    </Badge>
                    <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                      {item.category}
                    </Badge>
                    <Badge variant="outline" className={getDemandColor(item.demandLevel)}>
                      {item.demandLevel} popyt
                    </Badge>
                    <div className="flex items-center text-sm text-gray-500">
                      <MapPin className="h-3 w-3 mr-1" />
                      {item.region}
                    </div>
                    {item.remote && (
                      <Badge variant="outline" className="bg-[#2bcaca]/10 text-[#2bcaca] border-[#2bcaca]/20">
                        Praca zdalna
                      </Badge>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {item.benefits.slice(0, 3).map((benefit, index) => (
                      <span key={index} className="text-xs bg-[#2bcaca]/10 text-[#2bcaca] px-2 py-1 rounded-none border border-[#2bcaca]/20">
                        {benefit}
                      </span>
                    ))}
                    {item.benefits.length > 3 && (
                      <span className="text-xs bg-gray-50 text-gray-600 px-2 py-1 rounded-none border border-gray-200">
                        +{item.benefits.length - 3} więcej
                      </span>
                    )}
                  </div>
                </div>

                {/* Right Side - Salary Info */}
                <div className="lg:text-right lg:ml-6">
                  <div className="flex items-center lg:justify-end mb-2">
                    <DollarSign className="h-5 w-5 text-[#2bcaca] mr-1" />
                    <span className="text-2xl font-bold text-gray-900">
                      {item.averageSalary.toLocaleString()} {item.currency}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 mb-2">
                    {item.minSalary.toLocaleString()} - {item.maxSalary.toLocaleString()} {item.currency}
                  </div>
                  <div className="flex items-center lg:justify-end">
                    <TrendingUp className="h-4 w-4 text-[#2bcaca] mr-1" />
                    <span className="text-sm text-[#2bcaca] font-medium">+{item.growth}% wzrost</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredData.length === 0 && (
          <div className="text-center py-12">
            <Info className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Brak wyników</h3>
            <p className="text-gray-500">Nie znaleziono stanowisk spełniających wybrane kryteria</p>
          </div>
        )}

        {/* Summary Stats */}
        {filteredData.length > 0 && (
          <div className="mt-8 bg-white p-6 rounded-none border border-gray-200 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Podsumowanie wyników</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#2bcaca]">
                  {Math.round(
                    filteredData.reduce((sum, item) => sum + item.averageSalary, 0) / filteredData.length,
                  ).toLocaleString()}{" "}
                  zł
                </div>
                <div className="text-sm text-gray-500">Średnie zarobki</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#2bcaca]">
                  {Math.round((filteredData.reduce((sum, item) => sum + item.growth, 0) / filteredData.length) * 10) /
                    10}
                  %
                </div>
                <div className="text-sm text-gray-500">Średni wzrost</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#2bcaca]">
                  {Math.round((filteredData.filter((item) => item.remote).length / filteredData.length) * 100)}%
                </div>
                <div className="text-sm text-gray-500">Praca zdalna</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#2bcaca]">
                  {Math.round(
                    (filteredData.filter((item) => item.demandLevel === "Wysoki").length / filteredData.length) * 100,
                  )}
                  %
                </div>
                <div className="text-sm text-gray-500">Wysoki popyt</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
